import React, { useEffect, useId, useRef, useState, useSyncExternalStore } from 'react';
import { Box, CircularProgress, Popper, TextField, Typography } from '@mui/material';
import {
  LEAD_PROFILE_PLACE_NAME_MAX,
  PLACE_NAME_KINDS,
  PLACE_NAME_MIN_CHARS,
  getPlaceNameCoverage,
  getPlaceNamesErrorMessage,
  subscribePlaceNameCoverage,
  readPlaceNames,
  searchPlaceNames,
} from '../../services/leadsService';
import { LEAD_PROFILE_PLACE_KIND_LABELS } from '../leadProfile';
import LeadSearchAdornment from './LeadSearchAdornment';
import { FLOATING_MODIFIERS, floatingPopperSx, leadRecordDropdownSx } from './LeadRecordSearch';
import { getScrollEdgeFadeSx, useScrollEdgeFade } from '../../hooks/useScrollEdgeFade';
import { LEADS_TEXT_MUTED, LEADS_TEXT_PRIMARY, leadsTextFieldSx } from './leadsUi.styles';

export const PLACE_NAME_DEBOUNCE_MS = 300;
const PLACE_NAME_LIMIT = 8;

const resultsSx = {
  position: 'static',
  display: 'flex',
  flexDirection: 'column',
  gap: 0.25,
  maxHeight: 220,
  overflowY: 'auto',
};

const rowSx = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  px: 1,
  py: 0.75,
  borderRadius: '6px',
  textAlign: 'left',
  color: LEADS_TEXT_PRIMARY,
  cursor: 'pointer',
  '&:hover, &[data-active="true"]': { bgcolor: 'rgba(255,255,255,0.06)' },
};

const metaSx = {
  fontSize: '11px', color: LEADS_TEXT_MUTED, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};

/**
 * Provincias del catálogo: `coverage` de la última respuesta de
 * `/searchplacenames` (hoy Córdoba y San Luis; crece sin tocar el Front).
 */
export function usePlaceNameCoverage() {
  return useSyncExternalStore(subscribePlaceNameCoverage, getPlaceNameCoverage, getPlaceNameCoverage);
}

const NAME_MAX = LEAD_PROFILE_PLACE_NAME_MAX;
const kindLabel = (kind) => LEAD_PROFILE_PLACE_KIND_LABELS[kind] || kind;

/**
 * Buscador de lugares del catálogo (`/searchplacenames`, debounce 300 ms, ≥2
 * caracteres, cancela la búsqueda anterior). Muestra `label` (distingue
 * homónimos: varios «Güemes») y al elegir devuelve el **item entero** del
 * resultado (`{ kind, name, province, municipality, department, label }`, lo
 * que guarda `profile.residence_location`). Última fila «Otro: …» (si
 * `allowOther`): texto libre 1–120 → `{ kind: 'other', name }`, para lugares
 * fuera del catálogo o del exterior. `kind` vacío busca barrio, municipio y
 * provincia (nunca departamento); `province` acota la búsqueda, opcional.
 *
 * Recuperado de 59042a9 (antes era para `interest_filters.locations`).
 *
 * Mismo patrón flotante que `LeadPicker`: combobox + listbox en Popper, ↑ / ↓ +
 * Enter, Escape cierra la lista sin cerrar el diálogo / popover.
 *
 * @param {{ kind?: string, province?: string, onPick: (loc: { kind: string, name: string }) => void,
 *   allowOther?: boolean, disabled?: boolean, error?: boolean,
 *   placeholder?: string, ariaLabel?: string }} props
 */
export default function PlaceNameAutocomplete({
  kind = '',
  province = '',
  onPick,
  allowOther = true,
  disabled = false,
  error = false,
  placeholder = 'Buscar barrio o ciudad',
  ariaLabel = 'Buscar lugar',
}) {
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  const [state, setState] = useState({ status: 'idle', places: [], error: '' });
  const [dismissed, setDismissed] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const anchorRef = useRef(null);
  const listId = useId();
  const { scrollRef, fadeTop, fadeBottom } = useScrollEdgeFade();

  useEffect(() => {
    const handle = setTimeout(() => setQuery(text.trim()), PLACE_NAME_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [text]);


  useEffect(() => {
    if (query.length < PLACE_NAME_MIN_CHARS) {
      setState({ status: 'idle', places: [], error: '' });
      return undefined;
    }
    const controller = new AbortController();
    setState((prev) => ({ ...prev, status: 'loading', error: '' }));
    (async () => {
      try {
        const res = await searchPlaceNames(
          {
            q: query,
            kind: kind || PLACE_NAME_KINDS,
            ...(province ? { province } : {}),
            limit: PLACE_NAME_LIMIT,
          },
          { signal: controller.signal },
        );
        if (controller.signal.aborted) return;
        if (!res?._httpOk) {
          setState({ status: 'error', places: [], error: getPlaceNamesErrorMessage(res) });
          return;
        }
        setState({ status: 'ready', places: readPlaceNames(res), error: '' });
      } catch (err) {
        if (err?.name !== 'AbortError') setState({ status: 'error', places: [], error: 'No se pudo buscar el lugar.' });
      }
    })();
    return () => controller.abort();
  }, [query, kind, province]);

  useEffect(() => { setActiveIndex(-1); }, [state.places, query]);

  const trimmed = text.trim();
  const settled = query === trimmed && query.length >= PLACE_NAME_MIN_CHARS;
  const places = state.status === 'ready' ? state.places : [];
  const exact = places.some((p) => p.name.toLowerCase() === trimmed.toLowerCase());
  const showOther = allowOther && settled && !exact && state.status !== 'loading'
    && trimmed.length <= NAME_MAX;
  const options = [
    ...places.map((p) => ({ type: 'place', place: p })),
    ...(showOther ? [{ type: 'other', place: { kind: 'other', name: trimmed } }] : []),
  ];

  const pick = (option) => {
    onPick(option.type === 'other' ? { kind: 'other', name: option.place.name } : { ...option.place });
    setText('');
    setQuery('');
    setDismissed(true);
  };

  const optionId = (index) => `${listId}-opt-${index}`;
  let status = null;
  if (trimmed.length >= PLACE_NAME_MIN_CHARS) {
    if (state.status === 'loading' && !places.length) {
      status = <Box role="status" sx={{ display: 'flex', justifyContent: 'center', py: 1 }}><CircularProgress size={18} aria-label="Buscando" /></Box>;
    } else if (state.status === 'error') {
      status = <Typography role="alert" sx={{ ...metaSx, px: 1, py: 0.5 }}>{state.error}</Typography>;
    } else if (state.status === 'ready' && !places.length) {
      status = (
        <Typography role="status" sx={{ ...metaSx, px: 1, py: 0.5, whiteSpace: 'normal' }}>
          {province ? `Sin resultados en ${province}.` : 'Sin resultados en el catálogo.'}
        </Typography>
      );
    }
  }
  const open = !dismissed && !disabled && (Boolean(status) || options.length > 0);
  const hasOptions = open && options.length > 0;

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && open) {
      e.preventDefault();
      e.stopPropagation();
      setDismissed(true);
      return;
    }
    if (e.key === 'Enter') {
      // Nunca envía el formulario que lo contiene.
      e.preventDefault();
      if (hasOptions && activeIndex >= 0) pick(options[activeIndex]);
      return;
    }
    if (!hasOptions) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      const next = (activeIndex + step + options.length) % options.length;
      setActiveIndex(next);
      document.getElementById(optionId(next))?.scrollIntoView?.({ block: 'nearest' });
    }
  };

  return (
    <Box data-testid="place-name-autocomplete" sx={{ minWidth: 0 }}>
      <TextField
        ref={anchorRef}
        value={text}
        onChange={(e) => { setText(e.target.value); setDismissed(false); }}
        onFocus={() => setDismissed(false)}
        onBlur={() => setDismissed(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        size="small"
        fullWidth
        disabled={disabled}
        error={error}
        InputProps={{ startAdornment: <LeadSearchAdornment /> }}
        inputProps={{
          'aria-label': ariaLabel,
          maxLength: NAME_MAX,
          role: 'combobox',
          'aria-autocomplete': 'list',
          'aria-expanded': open,
          'aria-controls': open ? listId : undefined,
          'aria-activedescendant': hasOptions && activeIndex >= 0 ? optionId(activeIndex) : undefined,
        }}
        sx={leadsTextFieldSx}
      />
      <Popper
        open={open}
        anchorEl={() => anchorRef.current?.querySelector('.MuiOutlinedInput-root') || anchorRef.current}
        placement="bottom-start"
        modifiers={FLOATING_MODIFIERS}
        sx={floatingPopperSx}
        style={{ width: anchorRef.current?.querySelector('.MuiOutlinedInput-root')?.clientWidth || undefined }}
      >
        <Box
          id={listId}
          ref={scrollRef}
          role={hasOptions ? 'listbox' : 'region'}
          aria-label="Lugares"
          onMouseDown={(e) => e.preventDefault()}
          sx={{ ...leadRecordDropdownSx, ...resultsSx, ...getScrollEdgeFadeSx(fadeTop, fadeBottom) }}
        >
          {status}
          {options.map((option, index) => {
            const { place } = option;
            const isOther = option.type === 'other';
            return (
              <Box
                key={isOther ? 'other' : `${place.kind}-${place.label}`}
                id={optionId(index)}
                role="option"
                aria-selected={index === activeIndex}
                aria-label={isOther
                  ? `Otro: ${place.name} (texto libre)`
                  : `${place.label} (${kindLabel(place.kind).toLowerCase()})`}
                data-active={index === activeIndex}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => pick(option)}
                sx={isOther ? { ...rowSx, borderTop: places.length ? '1px solid rgba(255,255,255,0.08)' : 'none', mt: places.length ? 0.25 : 0 } : rowSx}
              >
                <Typography sx={{ fontSize: '13px', lineHeight: 1.3 }} noWrap>
                  {isOther ? `Otro: «${place.name}»` : place.label}
                </Typography>
                <Typography sx={metaSx}>
                  {isOther ? 'Texto libre · fuera del catálogo' : kindLabel(place.kind)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Popper>
    </Box>
  );
}
